import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { AlertTriangle, Calendar, CheckCircle2, Clock, Phone } from 'lucide-react';

export default function Closures() {
  const { t } = useTranslation();

  const upcomingClosures = [
    { title: 'Youth Day', date: 'February 11, 2026', description: 'All campuses closed.' },
    { title: 'Labour Day', date: 'May 1, 2026', description: 'All campuses closed.' },
    { title: 'National Day Holiday', date: 'May 20, 2026', description: 'All campuses closed for National Day celebrations.' },
    { title: 'Christmas Break', date: 'Dec 25, 2026 - Jan 1, 2027', description: 'All campuses closed for the holiday season.' }
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('nav_closures', 'Closures & Alerts')}
        description={t('closures_hero_desc', 'Important announcements about campus closures, holidays, and service disruptions.')}
        breadcrumbs={[{ label: t('nav_closures', 'Closures'), href: '/closures' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="admissions" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Hero image */}
          <div className="w-full h-48 md:h-64 overflow-hidden rounded-sm shadow-xl border-b-8 border-gold">
            <img src="/images/campus/building-01.jpg" alt="ESTAD Campus" className="w-full h-full object-cover" />
          </div>

          {/* Current Status Section */}
          <section id="status" className="space-y-10">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <AlertTriangle className="w-8 h-8 text-gold" /> {t('closures_status_title', 'Current Alerts')}
            </h2>
            
            <div className="flex items-center gap-6 p-8 bg-green-50 border-l-8 border-green-500 rounded-sm shadow-sm group hover:shadow-md transition-all">
               <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-green-500 shadow-sm flex-shrink-0 group-hover:scale-110 transition-transform">
                 <CheckCircle2 className="w-8 h-8" />
               </div>
               <div className="space-y-1">
                 <h3 className="text-xl font-black text-green-800 uppercase tracking-tighter leading-none">Normal Operations</h3>
                 <p className="text-green-700/70 font-medium">There are currently no active alerts. All campuses are operating as scheduled.</p>
               </div>
            </div>
          </section>

          {/* Upcoming Closures Grid */}
          <section id="upcoming" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <Calendar className="w-8 h-8 text-gold" /> {t('closures_upcoming_title', 'Upcoming Closures')}
            </h2>
            <div className="grid grid-cols-1 gap-6">
              {upcomingClosures.map((closure, idx) => (
                <div key={idx} className="flex flex-col md:flex-row md:items-center justify-between p-8 bg-gray-50 border border-gray-100 group hover:border-gold transition-all relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-navy group-hover:bg-gold transition-colors"></div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-black text-navy uppercase tracking-tighter group-hover:text-gold transition-colors leading-none">{closure.title}</h3>
                    <p className="text-gray-500 font-medium text-sm">{closure.description}</p>
                  </div>
                  <div className="mt-4 md:mt-0 flex items-center gap-4">
                     <div className="flex flex-col items-end">
                       <span className="text-gold font-black uppercase text-xs tracking-widest leading-none">Scheduled Date</span>
                       <span className="text-navy font-black text-lg uppercase tracking-tighter">{closure.date}</span>
                     </div>
                     <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-navy opacity-30 group-hover:opacity-100 group-hover:text-gold transition-all">
                       <Clock className="w-5 h-5" />
                     </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Emergency Contact Banner */}
          <section id="emergency" className="bg-navy p-12 md:p-16 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold/5 rounded-full translate-y-1/2 -translate-x-1/2 group-hover:scale-110 transition-transform duration-1000"></div>
            <div className="relative z-10 text-center space-y-8">
               <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic italic">
                {t('closures_emergency_title', 'Urgence & Sécurité')}
              </h2>
              <div className="max-w-2xl mx-auto space-y-8">
                <p className="text-white/60 font-medium leading-relaxed">
                  For emergencies during campus closures or to report a security incident, please contact our 24/7 Security Operations Center.
                </p>
                <a href="tel:+237233123999" className="inline-flex items-center gap-6 bg-gold text-navy px-12 py-5 font-black uppercase tracking-widest text-xl shadow-xl hover:brightness-110 transition-all active:scale-95 group/btn">
                  <Phone className="w-8 h-8 group-hover/btn:rotate-12 transition-transform" /> +237 233 123 999
                </a>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
